import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const languages = {
  en: 'English',
  uk: 'Українська',
  es: 'Español',
  fr: 'Français',
  pl: 'Polski',
  it: 'Italiano',
  pt: 'Português'
};

const SUPPORTED_LANGS = Object.keys(languages);

/** Globe + current language name, as `.locale` in the design; `up` opens the menu above (footer). */
export function LanguageSwitcher({ up = false }: { up?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const current = (SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : 'en') as keyof typeof languages;

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  const handleLanguageChange = (newLang: string) => {
    setOpen(false);
    i18n.changeLanguage(newLang);
    const pathWithoutLang = location.pathname.replace(new RegExp(`^/(${SUPPORTED_LANGS.join('|')})`), '');
    navigate(`/${newLang}${pathWithoutLang || '/'}`);
  };

  return (
    <div ref={root} className={`locale-switcher${up ? ' up' : ''}`}>
      <button
        type="button"
        className="locale"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${t('site.header.language')}: ${languages[current]}`}
        onClick={() => setOpen((value) => !value)}
      >
        <img src="/site/icons/Globe.svg" alt="" />
        {languages[current]}
      </button>
      {open && (
        <ul className="locale-menu">
          {Object.entries(languages).map(([code, name]) => (
            <li key={code}>
              <button type="button" lang={code} aria-current={code === current} onClick={() => handleLanguageChange(code)}>
                {name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
